import React, { Fragment } from 'react';
import { Typography } from '../Typography';
import ChangeReviewItem from './ChangeReviewItem';

export interface ChangeReviewRemovedDetail {
    label: string;
    /** A node, so a caller can show a chip or code rather than a string. */
    value: React.ReactNode;
}

export interface ChangeReviewRemovedProps {
    /** The noun for what was removed. Defaults to a capability, but the component isn't
     *  policy-specific — a caller reviewing something else names it. */
    title?: string;
    /** Field/value pairs describing the removed thing, so the admin can see what they would
     *  lose before deciding. */
    details: ChangeReviewRemovedDetail[];
    onRestore: () => void;
    onDiscard: () => void;
    /** What made the removal. Names the assistant by default. */
    origin?: string;
    disabled?: boolean;
    className?: string;
}

/**
 * A capability (or field, or clause — whatever the caller names it) the assistant proposed
 * removing outright. Unlike a plain `Item`, there is nothing left on screen to show the change
 * against, so this renders what the removed thing used to be.
 *
 * It IS an `Item` underneath, with the removed thing's details as its `children`. That is
 * deliberate: a removal is reviewed the same way every other change is, and a card of its own
 * design put a second visual language and a second pair of button labels in front of an admin
 * working down one column of changes. Accept removes it; revert keeps it.
 */
const ChangeReviewRemoved: React.FC<ChangeReviewRemovedProps> = ({
    title = 'Capability removed',
    details,
    onRestore,
    onDiscard,
    origin = 'assistant',
    disabled = false,
    className = '',
}) => (
    // The group wrapper is the card's handle: it names the whole removal for a screen reader
    // (and for anything looking the card up), which the `Item` inside cannot do for itself.
    <div role="group" aria-label={`${title} by ${origin}`}>
        <ChangeReviewItem
            kind="removed"
            label={title}
            origin={origin}
            disabled={disabled}
            className={className}
            onApprove={onDiscard}
            onDeny={onRestore}
        >
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                {details.map((detail) => (
                    <Fragment key={detail.label}>
                        <dt className="m-0">
                            <Typography.SecondaryText size="small">
                                {detail.label}
                            </Typography.SecondaryText>
                        </dt>
                        <dd className="m-0">
                            <Typography.Text size="small">{detail.value}</Typography.Text>
                        </dd>
                    </Fragment>
                ))}
            </dl>
        </ChangeReviewItem>
    </div>
);

export default ChangeReviewRemoved;
export { ChangeReviewRemoved };
